import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import { Radio, RadioGroup } from '@app/components/common/Radio/Radio';
import * as Auth from '@app/components/layouts/AuthLayout/AuthLayout.styles';
import { ADMIN_BASE_BATH } from '@app/constants/variables';
import { notificationController } from '@app/controllers/notificationController';
import { cryptoDecryptHandler, cryptoEncrpytHandler } from '@app/controllers/rememberMeController';
import { getLoginErrorMessage, IOtpValue, SendOtpHandler } from '@app/controllers/sendOtpController';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { readRememberMeToken } from '@app/services/localStorage.service';
import { doLogin } from '@app/store/slices/authSlice';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import * as S from './LoginForm.styles';

export const initValues: LoginFormData = {
  email: '',
  password: '',
  type: 'password',
  rememberMe: false,
};
interface LoginFormData {
  email?: string;
  password?: string;
  type?: string;
  rememberMe?: boolean;
}

interface UserDataInterFace {
  renderForm?: boolean;
  loginData: LoginFormData;
}

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [isLoading, setLoading] = useState(false);

  const [type, setType] = useState(initValues.type);

  const [userData, setUserData] = useState<UserDataInterFace>({
    renderForm: false,
    loginData: initValues,
  });

  const isTypePassword = type === 'password';

  const handleSubmit = (values: LoginFormData) => {
    const { email, password, rememberMe } = values;
    setLoading(true);
    if (isTypePassword) {
      const payload = {
        email: values.email,
        password: values.password,
        rememberMe: values.rememberMe,
      };
      dispatch(doLogin(payload))
        .unwrap()
        .then(() => {
          navigate(ADMIN_BASE_BATH);
          cryptoEncrpytHandler({ email, password, rememberMe });
        })
        .catch((err) => {
          notificationController.error({ message: getLoginErrorMessage(t, err.message) });
          setLoading(false);
        });
    } else {
      SendOtpHandler(
        values as IOtpValue,
        dispatch,
        navigate,
        t,
        () => {
          setLoading(true);
        },
        () => {
          setLoading(false);
        },
      );
    }
  };

  useEffect(() => {
    const rememberToken = readRememberMeToken();
    if (rememberToken) {
      const loginData: LoginFormData = cryptoDecryptHandler(rememberToken) ?? initValues;
      setUserData((prevState) => ({ ...prevState, loginData, renderForm: true }));
    } else {
      setUserData((prevState) => ({ ...prevState, ...initValues, renderForm: true }));
    }
  }, []);

  if (!userData.renderForm) return <></>;

  return (
    <Auth.FormWrapper>
      <BaseForm layout="vertical" onFinish={handleSubmit} initialValues={userData.loginData}>
        <Auth.FormTitle>{t('common.login')}</Auth.FormTitle>
        <S.LoginDescription>{t('login.loginInfo')}</S.LoginDescription>
        <Auth.FormItem
          name="email"
          label={t('common.email')}
          rules={[
            { required: true, message: t('common.requiredField') },
            {
              type: 'email',
              message: t('common.notValidEmail'),
            },
          ]}
        >
          <Auth.FormInput placeholder={t('common.email')} />
        </Auth.FormItem>
        <BaseForm.Item name="type" label={''}>
          <RadioGroup onChange={(e) => setType(e.target.value)}>
            <Radio value="password">{t('common.usePassword')}</Radio>
            <Radio value="otp">{t('common.useOtp')}</Radio>
          </RadioGroup>
        </BaseForm.Item>
        {isTypePassword && (
          <Auth.FormItem
            label={t('common.password')}
            name="password"
            rules={[{ required: true, message: t('common.requiredField') }]}
          >
            <Auth.FormInputPassword placeholder={t('common.password')} />
          </Auth.FormItem>
        )}
        <Auth.ActionsWrapper>
          <BaseForm.Item name="rememberMe" valuePropName="checked" noStyle>
            <Auth.FormCheckbox>
              <S.RememberMeText>{t('login.rememberMe')}</S.RememberMeText>
            </Auth.FormCheckbox>
          </BaseForm.Item>
          <Link to="/auth/forgot-password">
            <S.ForgotPasswordText>{t('common.forgotPass')}</S.ForgotPasswordText>
          </Link>
        </Auth.ActionsWrapper>
        <BaseForm.Item noStyle>
          <Auth.SubmitButton type="primary" htmlType="submit" loading={isLoading}>
            {t(!isTypePassword ? 'common.sendOtp' : 'common.login')}
          </Auth.SubmitButton>
        </BaseForm.Item>
      </BaseForm>
    </Auth.FormWrapper>
  );
};
